<template>
  <div>
    <select-field ref="LieuSummary"
                  :value=display
                  :required=required
                  :editFieldInfo=editFieldInfo
                  :items=items
                  :disabled=readOnly
                  :canEdit=canEdit
                  :selectText=selectText
                  :hideLabel='hideLabel'
                  :dataTable='dataTable'
                  :showMissing=showMissing
                  :notOrdered=notOrdered
                  :fiche=fiche
                  @updated=setUpdated
                  @onEdit=onEdit
                  @error=setError>
    </select-field> 

    <section-container-panel v-if="dialogOpen"
                             :section="section"
                             :dialogOpen="dialogOpen"
                             :extended="true"
                             :helper="helper"
                             :ressourceHeader="ressourceHeader"
                             :fiche="fiche"
                             @updated="setSectionUpdated"
                             @closeDialog="closeDialog"
                             @saveOrCancel="setSaveOrCancel"
                             @status="statusUpdated">
    </section-container-panel>
  </div>
</template>

<script lang="ts">
  import { defineComponent } from "vue";
  import Section from '@/framework/base/classes/Section';
  import { FieldInfo, EditFieldType, FieldStatus } from '@/framework/base/components/FieldInfo'
  import SummaryBase from '@/framework/base/components/SummaryBase';
  import { Sections } from '@/generated/classes/AppSchema';
  import SelectField from '@/framework/base/components/SelectField.vue'
  import Item from '@/framework/base/classes/Item';
  import Lieu from '@/extend/classes/Lieu';
  import Owner from '@/extend/classes/Owner';
  import SectionContainerPanel from '@/framework/base/components/SectionContainerPanel.vue';
  import SectionLieuExtend from '@/extend/sections/SectionLieuExtend.vue';
  import { storeLieu } from '@/generated/stores';
  import ListData, { ListFilter } from '@/extend/classes/ListData';

  export default defineComponent({
    extends: SummaryBase,
    components: {
      SelectField,
      SectionContainerPanel,
    },

    props: {
      helper: {
        type: Boolean
      }
    },

    beforeUnmount (): void {
      this.clearStore();
    },

    data() {
      const listLieu: Lieu[] = [];
      const tmpLieu: Lieu = new Lieu();
      return {
        selectedLieuId: 0,
        selectedLieu: new Lieu(),
        tmpLieu,
        newContext: true,
        reset: true,
        listUpdated: false,
        listLieu
      }
    },

    computed: {
      editFieldInfo (): FieldInfo {
        const type = EditFieldType.SUMMARY;
        const precision = 2;
        const format = null;
        const mask = null;
        const focus = null;
        const live = null;
        return new FieldInfo(this.ressource, type, precision, mask, format, focus, live, this.theme);
      },

      section (): Section {
        const section = new Section();
        section.component = SectionLieuExtend;
        section.type = 'lieu';
        section.data = this.selectedLieu;
        section.picto = 'SectionLieuExtendPicto';
        section.contextId = this.contextId;
        section.showButton = true;
        section.autoSave = true;
        if (this.sectionName) {
          section.sectionName = this.sectionName;
        }
        if (this.requiredAttributs) {
          section.requiredAttributs = this.requiredAttributs;
        }
        return section;
      },

      listItems (): Lieu[] {
        if (this.reset != this.toggleReset) {
          this.loadItems();
        }
        return this.listLieu;
      },

      items (): Item[] {
        const items: Item[] = [];
        items.push(new Item(0, this.newLabel != undefined && this.newLabel != '' ? this.newLabel : "Nouveau lieu")); 
        const lieus = this.getItems(this.listItems, this.sectionName);
        lieus.forEach(lieu => {
          if (lieu && lieu.id > 0 ) {
            items.push(new Item(lieu.id, this.getDisplay(lieu)));
          }
        })
        return items;
      },

      display (): string {
        const value = this.value;
        let item = this.items.find(a => a.key == value)
        this.changeContext();
        if (this.items.length == 2 && this.newContext && this.required && this.autoSelect) {
          this.changeContext();
          item = this.items.find(a => a.key == this.items[1].key);
          if (item && item.key != this.initValue) {
            this.itemChanged(item.key);
            this.setStatusUpdated(true);
            if (this.initValue != 0 || this.items.length != 2) {
              this.statusUpdated(this.status);
            }
          }
        }
        if (item) {
          if (this.resetToggle()) {
            this.setStatusUpdated(true);
            this.statusUpdated(this.status);
          }
          const itemDefined = item;
          const lieu = this.listLieu.find(a => a.id == itemDefined.key);
          if (lieu && lieu.id != this.selectedLieuId) {
            this.setSelectedLieu(lieu);
          }
          if (item.key == 0 && this.selectedLieuId == 0) {
            this.setSelectedLieu(this.newLieu());
          }
          this.setSelectedLieuId(lieu ? lieu.id : this.selectedLieu.id);
        }
        const res = item && item.key != 0 ? item.title : '';
        if (this.cancelChanges && item) {
          this.itemChanged(item.key);
        }
        this.setAttribut(this.selectedLieu, this.owner, this.parentObj)
        return res
      },

      canEdit (): string {
        return true
      }
    },

    methods: {
      setSelectedLieu(value: Lieu) {
        this.selectedLieu = value;
      },
      setSelectedLieuId(id: number) {
        this.selectedLieuId = id;
      },
      setNewContext(value: boolean) {
        this.newContext = value;
      },
      setStatusUpdated(value: boolean) {
        this.status.updated = value;
      },
      newLieu (): Lieu {
        this.clearStore();
        const newLieu = storeLieu.createNewLieu();
        this.tmpLieu = newLieu;
        return newLieu;
      },

      loadItems (): Promise<boolean> {
        this.reset = this.toggleReset;
        return new Promise((resolve) => {
          if (this.filter) {
            Lieu.list(this.filter)
              .then((data: ListData) => {
                const items = data.reccords as Lieu[];
                this.listLieu = [];
                items.forEach((item) => {
                  this.listLieu.push(Lieu.fromJson(item));
                })
                this.listUpdated = true;
                resolve(true)
              }).catch(() => {
                //Catch empty list
              });
          } else {
            resolve(false);
          }
        })
      },

      clearStore() : void {
        if (this.tmpLieu != null && this.tmpLieu.id < 0) {
          storeLieu.mLieuDeleted(this.tmpLieu.id);
        }
      },

      contextChanged() : void {
        this.newContext = true;
        this.selectedLieuId = 0;
        this.selectedLieu = new Lieu();
        this.initValue = this.value;
      },

      setSaveOrCancel () {
        this.cancelChanges = true;
      },

      // eslint-disable-next-line
      getItems(lieu: Lieu[], section: String): Lieu[] {
        const items: Lieu[] = [];
        lieu.forEach((item) => {
          items.push(Lieu.fromJson(item));
        })
        return items;
      },

        // eslint-disable-next-line
      getDisplay(lieu: Lieu): string {
        return '';
      },

      // eslint-disable-next-line
      setAttribut(lieu: Lieu, owner: Owner, parentObj: unknown): void {
        // to implement
      },

      setSectionUpdated(lieu: Lieu) {
        const item = this.items.find(a => a.key == lieu.id);
        if (!item && lieu.id > 0) {
          this.loadItems().then(() => {
            const item = this.items.find(a => a.key == lieu.id);
            if (item) {
              this.setUpdated(item);
            }
          });
        }
        if (item) {
          this.setUpdated(item);
        }
      },

      setUpdated (item: Item): void {
        if (item.key == 0) {
          this.dialogOpen = true;
          this.selectedLieu = this.newLieu();
        }
        this.itemChanged(item.key);
        this.status.updated = this.initValue != item.key;
        this.status.error = false;
        this.statusUpdated(this.status);
      },

      setRestored (lieu: Lieu) {
        this.selectedLieu = lieu;
      },

      itemChanged (id: number): void {
        this.$emit('update:value', id);
      },

      statusUpdated (status: FieldStatus): void {
        this.$emit('status', status);
      },

      missing (): boolean {
        // @ts-expect-error : generated method
        return this.$refs.LieuSummary.missing();
      },

      touch (): boolean {
        // @ts-expect-error : generated method
        return this.$refs.LieuSummary.touch();
      }
    },
  })
</script>
